import { create } from 'zustand'

import type { SessionUser as GeneratedSessionUser, TenantView } from '../../api/types.gen'
import { api, setAccessToken } from '../../api/client'
import { applyTheme } from '../../theme'

/*
 | Quién entra y a qué estudio. Los dos vienen generados del backend: el rol y
 | el estado los decide él, y tenerlos escritos aquí era la forma de que un día
 | dejaran de coincidir.
 */
export type SessionUser = GeneratedSessionUser
export type SessionTenant = TenantView

interface SessionState {
  user: SessionUser | null
  tenant: SessionTenant | null
  status: 'idle' | 'loading' | 'authenticated' | 'anonymous'
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  restore: () => Promise<void>
  /** Relee el tenant sin rehacer la sesión. Tras pagar, sobre todo. */
  refreshTenant: () => Promise<void>
}

/**
 * El access token vive SÓLO en memoria, nunca en localStorage.
 *
 * Un token en localStorage es legible por cualquier script inyectado (XSS) y
 * persiste tras cerrar el navegador. Al mantenerlo en memoria, un XSS sólo
 * alcanza la sesión en curso. La persistencia real la aporta el refresh token,
 * que va en cookie HttpOnly y JavaScript no puede leer.
 *
 * Coste: al recargar la página hay que llamar a /auth/refresh. Es un round-trip
 * a cambio de una superficie de ataque bastante menor.
 */
export const useSession = create<SessionState>((set) => ({
  user: null,
  tenant: null,
  status: 'idle',

  login: async (email, password) => {
    set({ status: 'loading' })

    const result = await api.post<{ data: { access_token: string; user: SessionUser } }>('/auth/login', {
      email,
      password,
    })

    setAccessToken(result.data.access_token)

    const me = await api.get<{ data: { user: SessionUser; tenant: SessionTenant } }>('/me')

    applyTheme(me.data.tenant.branding)
    set({ user: me.data.user, tenant: me.data.tenant, status: 'authenticated' })
  },

  /**
   * Vuelve a leer el tenant.
   *
   * Hace falta tras activar la cuenta: `tenant.status` se guarda al entrar y es
   * lo que mira RequireActiveAccount. Sin refrescarlo, quien acaba de pagar
   * sigue viéndose como `trialing` y el guardián lo devuelve a la pantalla de
   * la tarjeta una y otra vez — pagando, pero atrapado.
   */
  refreshTenant: async () => {
    const me = await api.get<{ data: { user: SessionUser; tenant: SessionTenant } }>('/me')

    applyTheme(me.data.tenant.branding)
    set({ user: me.data.user, tenant: me.data.tenant })
  },

  logout: async () => {
    await api.post('/auth/logout').catch(() => undefined)
    setAccessToken(null)
    set({ user: null, tenant: null, status: 'anonymous' })
  },

  restore: async () => {
    set({ status: 'loading' })

    try {
      const refreshed = await api.post<{ data: { access_token: string } }>('/auth/refresh')
      setAccessToken(refreshed.data.access_token)

      const me = await api.get<{ data: { user: SessionUser; tenant: SessionTenant } }>('/me')

      applyTheme(me.data.tenant.branding)
      set({ user: me.data.user, tenant: me.data.tenant, status: 'authenticated' })
    } catch {
      setAccessToken(null)
      set({ user: null, tenant: null, status: 'anonymous' })
    }
  },
}))
